/**
 * Lightweight analytics helper.
 * Wraps gtag() with type-safe ferry events.
 * Falls back to console.log in dev / when gtag is unavailable.
 *
 * Schema validation:
 * - DEV: console.warn on missing required params
 * - PROD: log as Sentry breadcrumb (no throw)
 */
import { validateEventParams } from '@/lib/analyticsDashboard';
import { getAnonUserId } from '@/lib/userIdentity';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    __SENTRY__?: { hub?: { getClient?: () => { addBreadcrumb?: (b: unknown) => void } } };
  }
}

type FerryEvent =
  | 'favorite_set'
  | 'favorite_removed'
  | 'camera_opened'
  | 'departure_confidence_shown'
  | 'monitor_panel_viewed'
  | 'ferry_search'
  | 'ferry_nearby_used'
  | 'quick_open_favorite'
  | 'quick_open_recent_stop'
  | 'quick_open_recent_search'
  | 'ferry_shared'
  | 'ferry_share_copied'
  | 'ferry_calendar_added'
  | 'ferry_deeplink_opened'
  | 'ferry_dock_page_viewed'
  | 'favorite_set_from_dock_page'
  | 'shared_from_dock_page'
  | 'ferry_route_page_viewed'
  | 'favorite_route_set'
  | 'favorite_route_removed'
  | 'favorite_route_opened_from_home'
  | 'shared_from_route_page'
  | 'dashboard_autorefresh_enabled'
  | 'dashboard_autorefresh_suppressed'
  | 'ferry_price_section_opened'
  | 'favorite_tooltip_shown'
  | 'favorite_tooltip_clicked'
  | 'favorite_auto_suggested'
  | 'favorite_auto_accepted'
  | 'favorite_auto_dismissed'
  | 'search_nudge_shown'
  | 'search_nudge_dismissed'
  | 'search_nudge_accepted'
  | 'urgency_bar_shown'
  | 'favorite_feedback_shown'
  | 'route_card_clicked'
  | 'recommended_card_impression'
  | 'secondary_card_impression'
  | 'push_opted_in'
  | 'push_opted_out'
  | 'push_dock_toggled'
  | 'push_route_toggled'
  | 'push_snoozed'
  | 'push_quiet_hours_set'
  | 'traffic_badge_clicked'
  | 'traffic_low_shown'
  | 'traffic_medium_shown'
  | 'traffic_high_shown'
  | 'commuter_route_saved'
  | 'commuter_route_removed'
  | 'commuter_suggestion_shown'
  | 'commuter_suggestion_dismissed'
  | 'commuter_refresh_clicked'
  | 'commuter_inline_alert_shown'
  | 'commuter_route_opened_from_pinned'
  | 'commuter_alert_action_clicked'
  | 'commuter_smart_direction_used'
  | 'commuter_push_readiness_logged'
  | 'confidence_details_clicked'
  | 'push_opt_in_clicked'
  | 'push_permission_granted'
  | 'push_permission_denied'
  | 'least_queue_viewed'
  | 'returning_user_viewed'
  | 'blank_state_shown'
  | 'offline_cache_used'
  | 'push_gate_evaluated'
  | 'push_gate_blocked'
  | 'push_gate_would_send'
  | 'push_prefs_updated'
  | 'push_sw_error'
  | 'push_sent'
  | 'push_failed'
  | 'push_unsubscribed'
  | 'push_test_sent'
  | 'push_cron_triggered'
  | 'push_sensitivity_changed'
  | 'driving_mode_started'
  | 'driving_mode_expired'
  | 'queue_reported'
  | 'queue_prediction_viewed'
  | 'push_onboarding_shown'
  | 'push_onboarding_accepted'
  | 'push_onboarding_dismissed'
  | 'hero_auto_gps_triggered'
  | 'hero_gps_clicked'
  | 'map_follow_ferry_clicked'
  | 'reach_calculator_used'
  | 'reach_calculator_buffer_changed'
  | 'reach_calculator_fallback_shown'
  | 'map_night_mode_active'
  | 'map_route_shared'
  | 'hero_mode_switched'
  | 'hero_show_full_route_clicked'
  | 'hero_show_anyway_clicked'
  | 'onboarding_welcome_shown'
  | 'onboarding_welcome_dismissed'
  | 'ferry_favorites_migrated'
  | 'ferry_favorite_added'
  | 'ferry_favorite_removed'
  | 'ferry_favorite_synced'
  | 'ferry_favorite_pending'
  | 'my_ferries_expanded'
  | 'my_ferries_collapsed'
  | 'route_alert_created'
  | 'route_alert_toggled'
  | 'route_alert_deleted'
  | 'route_alert_triggered'
  | 'favorite_alert_sheet_opened'
  | 'alerts_master_toggled'
  | 'data_trust_strip_shown'
  | 'data_trust_retry_clicked'
  | 'mineferger_onboarding_shown'
  | 'mineferger_onboarding_step_clicked'
  | 'mineferger_onboarding_dismissed'
  | 'mineferger_onboarding_completed'
  | 'pro_upsell_shown'
  | 'pro_upsell_clicked'
  | 'pro_gate_blocked'
  | 'favorites_inventory_snapshot'
  | 'favorites_migration';

function isAnalyticsDebugEnabled(): boolean {
  try { return localStorage.getItem('debugAnalytics') === '1'; } catch { return false; }
}

// Stable 8-hex-char session ID per page load (crypto-safe when available)
const SESSION_ID: string = (() => {
  try {
    const buf = new Uint8Array(4);
    crypto.getRandomValues(buf);
    return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return Math.random().toString(16).slice(2, 10).padEnd(8, '0');
  }
})();

export function trackEvent(event: FerryEvent, params?: Record<string, string | number | boolean>) {
  const debug = isAnalyticsDebugEnabled();
  const hasGtag = typeof window.gtag === 'function';
  const enriched = { ...params, sessionId: SESSION_ID, anonUserId: getAnonUserId() };

  // Schema validation
  const validation = validateEventParams(event, enriched);
  if (!validation.valid) {
    const msg = `[analytics] Missing params for "${event}": ${validation.missing.join(', ')}`;
    if (import.meta.env.DEV) {
      console.warn(msg);
    } else {
      // Sentry breadcrumb (non-blocking)
      try {
        const Sentry = window.__SENTRY__;
        Sentry?.hub?.getClient?.()?.addBreadcrumb?.({
          category: 'analytics',
          message: msg,
          level: 'warning',
        });
      } catch { /* noop */ }
    }
  }

  if (hasGtag) {
    window.gtag?.('event', event, enriched);
  }

  if (debug || (!hasGtag && import.meta.env.DEV)) {
    // Never print full anonUserId / proof in console logs.
    const { anonUserId: _omitAnon, ...safe } = enriched;
    console.log('[analytics]', event, {
      ...safe,
      anonUserIdPrefix: typeof enriched.anonUserId === 'string' ? enriched.anonUserId.slice(0, 4) : undefined,
      sentTo: hasGtag ? 'gtag' : 'console_only',
    });
  }
}
